// Dependencies
import React from "react";

// Modules
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

// App
import { serverUrl, apiEndpoints } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import PostBody from "../../partials/postBody";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import CommentSection from "../../partials/comments/commentSection";
import NoImage from "../../partials/noImage";
import { logCustomEvent } from "../../core/analytics";
import Reactions from "../../partials/comments/reactions";
import { favoritesContentType } from "../../utils/helpers";

// UI components
import SinglesHeader from "../../partials/singlesHeader";
import { Row } from "react-bootstrap";
import MicNoneIcon from '@mui/icons-material/MicNone';
import { Typography } from "@mui/material";

// Dummy data

export default function PodcastSingle(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [imageError, setImageError] = useState(false);

  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");
  const contentType = favoritesContentType("podcast");

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line
  }, []);

  const loadNode = () => {
    const nid = params.id;

    if (nid) {
      getContent(nid);
    } else {
      setErrorStatus(404);
      setErrorMessage("Not found");
      setIsError(true);
    }
  };

  /**
   * @function getContent
   * @description Retrieves the data from an API / Fallback to local realm object if there is no connection
   */
  const getContent = (nid) => {
    let path = `${apiEndpoints.podcasts}/all/${nid}?_format=json&status=1&promote=1`;

    if (hash) {
      path = `/api/v2/shared?bundle=podcast&nid=${nid}&authkey=${hash}`;
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.data.rows.length > 0) {
          setPost(response.data.rows[0]);
          logCustomEvent("read", response.data.rows[0], "Podcast");
          setIsError(false);
          setIsLoading(false);
        } else {
          setErrorStatus(404);
          setErrorMessage("No data");
          setIsError(true);
        }
      })
      .catch((_error) => {
        if (_error?.response?.status === 403) {
          setErrorStatus(403);
          setErrorMessage("You do not have access to view this content.");
          setIsError(true);
          setIsLoading(false);
        } else {
          setErrorStatus(500);
          setErrorMessage("Failed to get data");
          setIsError(true);
          setIsLoading(false);
        }
      });
  };

  const renderPostAudio = (post) => {
    const img =
      post.field_featured_image.indexOf(serverUrl) > -1
        ? post.field_featured_image
        : `${serverUrl}${post.field_featured_image}`;

      let audioSrc = `${serverUrl}${post.field_audio}`;

      if (hash) {
        audioSrc = `${serverUrl}/shared/files/${post.filename}?access_token=${hash}`
      }

    return (
      <div className="post-podcast-container">
        <div className="img-container ratio ratio-16x9 w-100">
          <img
            src={imageError ? NoImage() : img}
            alt=""
            onError={() => setImageError(true)}
            className="podcast-img"
          />
        </div>
        <audio controls className="post-podcast w-100 mt-4 mb-3">
          <source
            src={audioSrc}
            alt={`Audio for ${post.title}`}
          />
        </audio>
        <div className="artist-container d-flex align-items-center justify-content-center mb-4">
          <div className="artist">
            <Typography
              className="d-flex"
              owner="client"
              variant="body1"
              color="clientPrimary.main"
            >
              <MicNoneIcon className="me-1" sx={{fontSize: 20}} />
              {post.field_artist}
            </Typography>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <SkeletonSingleScreen />;
  } else {
    if (isError) {
      return <Error status={errorStatus} message={errorMessage} />;
    } else {
      let category = "Uncategorised";

      if (post.category_labels) {
        category = post.category_labels;
      }

      return (
        <main className="podcasts  single">
          <article className="article">
            <SinglesHeader
              route={"/podcasts"}
              backBtnText={"Podcasts"}
              category={category}
              timestamp={post.created}
              title={post.title}
              hash={hash}
            />
            {renderPostAudio(post)}
            <PostBody body={post.body} />
            {user && user.current_user && (
              <>
                <Row className="reaction-container gx-0">
                  <Reactions
                    user={user}
                    nid={post.nid}
                    contentType={contentType}
                    field_allow_reactions={post.field_allow_reactions}
                    field_access_token={post.field_access_token}
                    title={post.title}
                    shareEnabled
                  />
                </Row>
                <CommentSection
                  nid={post.nid}
                  field_allow_reactions={post.field_allow_reactions}
                  field_allow_comments={post.field_allow_comments}
                />
              </>
            )}
          </article>
        </main>
      );
    }
  }
}
